import ApiService from '@/util/api.service';
import radioAudioBridge from '@/util/radioAudioBridge';

// ─── Time-Out Timer (TOT) ───
// Backstop de seguridad: corta la transmisión automáticamente tras
// RADIO_TOT_MS continuos de "querer hablar" (wantTalk). Evita que un keyup
// perdido (pedal USB desconectado, hipo del driver) deje el mic pegado
// jameando el canal compartido. Es el estándar de la radio real
// ("rekey to continue"). Cubre los tres disparadores de PTT (botón del panel,
// Ctrl+Espacio, pedal) porque todos pasan por demandFloor/ceaseFloor.
const RADIO_TOT_MS = 90000;
// Handle del timer a nivel de módulo (NO reactivo, como los timers del
// proyecto). Solo hay una TX a la vez, así que un único handle basta.
let totTimer = null;

function disarmTot() {
  if (totTimer) {
    clearTimeout(totTimer);
    totTimer = null;
  }
}

// Arma el TOT. Se llama UNA vez por pulsación (en demandFloor) y NO se resetea
// con los eventos 'floor' entrantes: el propósito es cortar también una TX
// legítima demasiado larga, obligando a soltar y re-pulsar.
function armTot(dispatch, commit) {
  disarmTot();
  totTimer = setTimeout(() => {
    totTimer = null;
    // Señal transitoria (timestamp que cambia en cada corte) para que
    // RadioTetraPanel muestre el hint. PATCH_ACTIVE es no-op si ya no hay call.
    commit('PATCH_ACTIVE', { totCutoffAt: Date.now() });
    // Cede la palabra por el camino normal (POST /floor/cease + mic off).
    dispatch('ceaseFloor');
  }, RADIO_TOT_MS);
}

const defaultState = () => ({
  active: null,
  micPermissionModal: { show: false, pendingCall: null },
});

const getters = {
  active: (state) => state.active,
  isVisible: (state) => state.active !== null,
  micPermissionModal: (state) => state.micPermissionModal,
};

const actions = {
  async placeCall({ commit }, {
    callId, peerName, peerId, provider = 'mcx'
  }) {
    commit('SET_ACTIVE', {
      callId,
      provider,
      peerName: peerName || `id=${peerId}`,
      peerId,
      state: 'alerting',
      floor: 'None',
      remoteSpeaker: null,
      holderKind: null,
      pendingFloor: false,
      wantTalk: false,
      audioReady: false,
      audioError: null,
      startedAt: Date.now(),
    });
    try {
      await radioAudioBridge.open(callId, provider);
      commit('PATCH_ACTIVE', { audioReady: true });
    } catch (err) {
      console.warn('audio bridge open failed', err);
      commit('PATCH_ACTIVE', { audioError: String(err?.message ?? err) });
    }
  },

  onEvent({ commit, state, dispatch }, { type, data }) {
    if (!state.active || data?.callId !== state.active.callId) return;
    switch (type) {
      case 'alerting':
        commit('PATCH_ACTIVE', { state: 'alerting' });
        break;
      case 'connected':
        // Hold-to-talk: la llamada CONECTA en ESCUCHA (floor None, mic off).
        // El operador mantiene el PTT para tomar la palabra (demandFloor →
        // el backend otorga → el evento 'floor' habilita el mic). Antes
        // arrancaba en 'Granted' (transmitiendo), que no es PTT real.
        commit('PATCH_ACTIVE', { state: 'connected', floor: 'None' });
        radioAudioBridge.setMicEnabled(false);
        break;
      case 'terminated':
        disarmTot();
        commit('PATCH_ACTIVE', {
          state: 'terminated',
          floor: 'None',
          // Causa del backend MCX si viene (nombre de campo defensivo). El
          // panel la muestra en el feedback "Sin respuesta" cuando aplica.
          endReason: (data && (data.reason || data.cause || data.message
            || data.code || data.disconnectReason)) || null,
        });
        radioAudioBridge.close(state.active.callId);
        setTimeout(() => commit('CLEAR'), 3000);
        break;
      case 'floor': {
        const floor = data?.floor ?? 'None';
        // holderKind ('radio'|'operator') solo lo manda el bridge TETRA en RemoteSpeaking.
        // Si es 'operator' otro despachador habla y NO viene speaker. MCX no lo envía → null.
        const holderKind = floor === 'RemoteSpeaking' ? (data?.holderKind ?? null) : null;
        const remoteSpeaker = floor === 'RemoteSpeaking'
          ? (data?.speaker ?? data?.holder ?? data?.number ?? null)
          : null;
        commit('PATCH_ACTIVE', {
          floor, remoteSpeaker, holderKind, pendingFloor: false,
        });
        // Solo abrimos el mic si el floor está concedido Y el usuario sigue pulsando.
        // Evita que un Granted que llega tarde reabra el mic tras soltar el PTT.
        radioAudioBridge.setMicEnabled(floor === 'Granted' && state.active.wantTalk);
        // La request en vuelo terminó (pendingFloor=false): aplica la última
        // intención del usuario por si pulsó/soltó mientras estaba pendiente.
        dispatch('reconcileFloor');
        break;
      }
      default:
        break;
    }
  },

  // El botón PTT solo registra la INTENCIÓN del usuario (wantTalk). La sincronización
  // real con el backend la hace reconcileFloor, que respeta una request en vuelo y
  // aplica el último estado deseado cuando esta termina. Así un press/release rápido
  // (o doble toque) no se traba: la intención final siempre se reconcilia.
  demandFloor({ state, commit, dispatch }) {
    if (!state.active) return;
    commit('PATCH_ACTIVE', { wantTalk: true });
    armTot(dispatch, commit);
    dispatch('reconcileFloor');
  },

  ceaseFloor({ state, commit, dispatch }) {
    if (!state.active) return;
    commit('PATCH_ACTIVE', { wantTalk: false });
    disarmTot();
    dispatch('reconcileFloor');
  },

  async reconcileFloor({ state, commit }) {
    const a = state.active;
    if (!a || a.pendingFloor) return; // hay una request en vuelo; al volver su evento se reintenta
    const {
      callId, provider, wantTalk, floor,
    } = a;
    const talking = floor === 'Granted' || floor === 'Demanding';
    if (wantTalk && !talking) {
      commit('PATCH_ACTIVE', { floor: 'Demanding', pendingFloor: true });
      try {
        await ApiService({
          url: `/v2/radio/calls/${callId}/floor/demand`,
          method: 'post',
          params: { type: provider },
        });
      } catch (err) {
        console.warn('demand floor error', err?.message ?? err);
        commit('PATCH_ACTIVE', { floor: 'Denied', pendingFloor: false });
      }
    } else if (!wantTalk && talking) {
      commit('PATCH_ACTIVE', { floor: 'None', pendingFloor: true });
      try {
        await ApiService({
          url: `/v2/radio/calls/${callId}/floor/cease`,
          method: 'post',
          params: { type: provider },
        });
      } catch (err) {
        console.warn('cease floor error', err?.message ?? err);
        commit('PATCH_ACTIVE', { pendingFloor: false });
      }
    } else if (wantTalk && floor === 'Granted') {
      // El backend ya nos tiene el floor (p.ej. auto-otorgado al conectar la
      // llamada directa): no hay que pedirlo, pero sí asegurar que el mic
      // esté abierto. Sin esto, el evento 'floor' Granted pudo llegar con
      // wantTalk=false (mic off) y al mantener el PTT después no se reabría.
      radioAudioBridge.setMicEnabled(true);
    }
  },

  async hangup({ state, commit }) {
    if (!state.active) return;
    disarmTot();
    const { callId, provider } = state.active;
    // Capturamos si teníamos el floor ANTES de resetearlo (para el cease).
    const wasTalking = state.active.floor === 'Granted';
    // floor:'None' junto con terminated: sin esto, radioTalking (floor==='Granted')
    // seguía true durante los ~500ms hasta CLEAR y la UI parpadeaba como
    // "transmitiendo"/activa al colgar. Un call terminado no tiene floor.
    // localHangup:true marca que terminó por acción del operador (no por el
    // backend), para que el panel NO muestre "Sin respuesta" si colgó durante
    // el ring (alerting→terminated local ≠ radio no contesta).
    // La UI pasa a terminated YA (instantáneo); el cease/DELETE van detrás.
    commit('PATCH_ACTIVE', { state: 'terminated', floor: 'None', localHangup: true });
    try { await radioAudioBridge.close(callId); } catch (_err) { /* ignore */ }
    // Si colgamos HABLANDO (floor tomado), liberamos el floor en el backend
    // ANTES del DELETE: algunos gateways MCX dejan la llamada colgada en el
    // radio si se termina con el floor aún tomado (caso "cuelgo hablando y el
    // radio queda abierto").
    if (wasTalking) {
      try {
        await ApiService({
          url: `/v2/radio/calls/${callId}/floor/cease`,
          method: 'post',
          params: { type: provider },
        });
      } catch (e) { console.warn('cease-before-hangup error', e?.message ?? e); }
    }
    try {
      await ApiService({
        url: `/v2/radio/calls/${callId}`,
        method: 'delete',
        params: { type: provider },
      });
    } catch (err) {
      console.warn('hangup error', err?.message ?? err);
    } finally {
      setTimeout(() => commit('CLEAR'), 500);
    }
  },

  clear({ commit }) {
    commit('CLEAR');
  },

  showMicPermissionModal({ commit }, pendingCall) {
    commit('SET_MIC_PERMISSION_MODAL', { show: true, pendingCall: pendingCall ?? null });
  },
  hideMicPermissionModal({ commit }) {
    commit('SET_MIC_PERMISSION_MODAL', { show: false, pendingCall: null });
  },
};

const mutations = {
  SET_ACTIVE(state, payload) {
    state.active = payload;
  },
  PATCH_ACTIVE(state, patch) {
    if (state.active) state.active = { ...state.active, ...patch };
  },
  CLEAR(state) {
    disarmTot();
    state.active = null;
  },
  SET_MIC_PERMISSION_MODAL(state, payload) {
    state.micPermissionModal = payload;
  },
};

export default {
  namespaced: true,
  state() {
    return defaultState();
  },
  getters,
  actions,
  mutations,
};
